// src/pages/BroadcastSms.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import axiosInstance from '../services/axiosInstance';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';

// SMSlenz's own documented per-message ceiling (send-sms / send-bulk-sms
// "message" field: max 1500 chars) — the only actual hard stop, since
// anything beyond it would just be rejected by their API outright.
const PROVIDER_MAX_LENGTH = 1500;

// GSM-7 (plain English/ASCII) messages get 160 chars for a single SMS,
// 153/segment once split into multiple. Unicode messages (Sinhala, Tamil,
// emoji, or any other non-ASCII content) get 70/segment single, 67/segment
// once split — SMS gateways reserve a few characters per part for the
// concatenation header once a message needs more than one segment.
function getSmsSegmentInfo(text) {
  const length = text.length;
  const isUnicode = /[^\x00-\x7F]/.test(text);
  const singleLimit = isUnicode ? 70 : 160;
  const multiLimit = isUnicode ? 67 : 153;

  let segments = 0;
  if (length > 0) {
    segments = length <= singleLimit ? 1 : Math.ceil(length / multiLimit);
  }

  return { length, isUnicode, singleLimit, multiLimit, segments };
}

const BroadcastSms = () => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const [allCustomers, setAllCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]); // array of full customer objects — PrimeReact's checkbox selection model

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [saveTemplateVisible, setSaveTemplateVisible] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateDraftMessage, setTemplateDraftMessage] = useState('');
  const [lastSentMessage, setLastSentMessage] = useState('');

  const sendingRef = useRef(false);

  const smsInfo = useMemo(() => getSmsSegmentInfo(message), [message]);

  const eligibleCustomers = useMemo(
    () => allCustomers.filter(c => !!c.phone_number),
    [allCustomers]
  );

  const excludedCount = allCustomers.length - eligibleCustomers.length;

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligibleCustomers;
    return eligibleCustomers.filter(c => {
      const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      return name.includes(q) || (c.phone_number || '').includes(q);
    });
  }, [eligibleCustomers, search]);

  useEffect(() => {
    setLoadingCustomers(true);
    // ?all=1 tells GFPagination to skip pagination entirely
    axiosInstance.get('/api/customer/?all=1')
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setAllCustomers(data);
        setSelected(data.filter(c => !!c.phone_number));
      })
      .catch(handleApiError)
      .finally(() => setLoadingCustomers(false));
  }, []);

  const loadTemplates = () => {
    axiosInstance.get('/api/broadcast-sms/templates/')
      .then(res => setTemplates(Array.isArray(res.data) ? res.data : []))
      .catch(handleApiError);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleSelectAll = () => setSelected(eligibleCustomers);
  const handleClearSelection = () => setSelected([]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) setMessage(template.message.slice(0, PROVIDER_MAX_LENGTH));
  };

  const openSaveTemplateDialog = (messageToSave) => {
    setTemplateDraftMessage(messageToSave);
    setNewTemplateName('');
    setSaveTemplateVisible(true);
  };

  const handleSaveTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) {
      handleApiError({ Error: 'Template name is required.' });
      return;
    }
    if (!templateDraftMessage.trim()) {
      handleApiError({ Error: 'Message cannot be empty.' });
      return;
    }
    setSavingTemplate(true);
    try {
      await axiosInstance.post('/api/broadcast-sms/templates/', { name, message: templateDraftMessage });
      handleApiSuccess('Template saved.');
      setSaveTemplateVisible(false);
      setNewTemplateName('');
      setLastSentMessage('');
      loadTemplates();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = () => {
    const template = templates.find(t => t.id === selectedTemplateId);
    if (!template) return;
    confirmDialog({
      message: `Delete template "${template.name}"? This cannot be undone.`,
      header: 'Delete Template',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          await axiosInstance.delete(`/api/broadcast-sms/templates/${template.id}/`);
          handleApiSuccess('Template deleted.');
          setSelectedTemplateId(null);
          loadTemplates();
        } catch (err) {
          handleApiError(err);
        }
      },
    });
  };

  const doSend = async () => {
    if (sendingRef.current) return; // already sending — ignore any duplicate trigger
    sendingRef.current = true;
    setSending(true);
    try {
      const res = await axiosInstance.post('/api/broadcast-sms/', {
        message,
        customer_ids: selected.map(c => c.id),
      });
      handleApiSuccess(res.data.detail || 'Broadcast queued.');
      setLastSentMessage(message);
      setMessage('');
    } catch (err) {
      handleApiError(err);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const handleSendClick = () => {
    if (!message.trim()) {
      handleApiError({ Error: 'Message cannot be empty.' });
      return;
    }
    if (selected.length === 0) {
      handleApiError({ Error: 'Select at least one customer to send to.' });
      return;
    }

    const segmentNote = smsInfo.segments > 1
      ? ` This message is ${smsInfo.segments} SMS segments long and will be charged as ${smsInfo.segments} messages per recipient.`
      : '';

    confirmDialog({
      message: `This will send an SMS to ${selected.length} customer(s).${segmentNote} This cannot be undone. Continue?`,
      header: 'Confirm Broadcast',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Send Broadcast',
      rejectLabel: 'Cancel',
      accept: doSend,
    });
  };

  return (
    <div className="containers" style={{ display: 'flex', justifyContent: 'center' }}>
      <div
        className="surface-card p-4 border-round shadow-1"
        style={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}
      >
        <h5 style={{ textAlign: 'center' }}>Broadcast SMS</h5>
        <p className="text-color-secondary mb-3" style={{ textAlign: 'center' }}>
          Pharmacy closures, holiday notices, promotions, etc.
        </p>

        <div className="field" style={{ marginBottom: '1rem' }}>
          <label className="font-bold" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Saved Templates:
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Dropdown
              value={selectedTemplateId}
              options={templates}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => handleTemplateSelect(e.value)}
              placeholder="Load a saved template..."
              filter
              showClear
              style={{ flex: 1 }}
              emptyMessage="No saved templates yet."
            />
            <Button
              icon="pi pi-trash"
              className="p-button-outlined p-button-danger"
              disabled={!selectedTemplateId}
              onClick={handleDeleteTemplate}
              tooltip="Delete selected template"
            />
          </div>
        </div>

        <div className="field" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label htmlFor="broadcast-message" className="font-bold">
              Message:
            </label>
            <Button
              label="Save as Template"
              icon="pi pi-save"
              className="p-button-sm p-button-text"
              onClick={() => openSaveTemplateDialog(message)}
            />
          </div>
          <InputTextarea
            id="broadcast-message"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, PROVIDER_MAX_LENGTH))}
            rows={5}
            style={{ width: '100%', display: 'block' }}
            placeholder="e.g. We will be closed on 13th & 14th April for the New Year holiday."
          />
          {lastSentMessage && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.75rem',
                marginTop: '0.75rem',
                padding: '0.6rem 0.85rem',
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: '6px',
              }}
            >
              <span style={{ fontSize: '0.85rem', color: '#065f46' }}>
                <i className="pi pi-check-circle" style={{ marginRight: '0.4rem' }} />
                Message delivered. Save it as a template for reuse?
              </span>
              <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                <Button
                  label="Save as Template"
                  icon="pi pi-save"
                  className="p-button-sm"
                  onClick={() => openSaveTemplateDialog(lastSentMessage)}
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-sm p-button-text"
                  onClick={() => setLastSentMessage('')}
                  tooltip="Dismiss"
                />
              </div>
            </div>
          )}
          <div style={{ marginTop: '0.4rem' }}>
            <small
              className={smsInfo.segments > 1 ? undefined : 'text-color-secondary'}
              style={smsInfo.segments > 1 ? { color: '#dc2626', fontWeight: 600 } : undefined}
            >
              {smsInfo.length} characters
              {smsInfo.isUnicode && ' (contains Sinhala/Tamil or other non-English characters)'}
              {' — '}
              {smsInfo.segments <= 1
                ? `fits in 1 SMS (up to ${smsInfo.singleLimit} chars)`
                : `will be sent as ${smsInfo.segments} SMS segments (over ${smsInfo.singleLimit} chars) — charged as ${smsInfo.segments} messages per recipient`
              }
            </small>
          </div>
          <div>
            <small className="text-color-secondary">
              This message will be sent to <strong>{selected.length}</strong> of{' '}
              {eligibleCustomers.length} eligible customer{eligibleCustomers.length === 1 ? '' : 's'}.
              {excludedCount > 0 && (
                <> {excludedCount} customer{excludedCount === 1 ? '' : 's'} excluded — no phone number on file.</>
              )}
            </small>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', marginBottom: '0.5rem' }}>
          <span className="font-bold">Recipients</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button label="Select All" icon="pi pi-check-square" className="p-button-sm p-button-outlined" onClick={handleSelectAll} />
            <Button label="Clear Selection" icon="pi pi-times" className="p-button-sm p-button-outlined" onClick={handleClearSelection} />
          </div>
        </div>

        <div style={{ position: 'relative', width: '100%', marginBottom: '0.75rem' }}>
          <i
            className="pi pi-search"
            style={{
              position: 'absolute',
              top: '50%',
              left: '0.75rem',
              transform: 'translateY(-50%)',
              color: '#6b7280',
              pointerEvents: 'none',
            }}
          />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name or phone number"
            style={{ width: '100%', paddingLeft: '2.25rem' }}
          />
        </div>

        <DataTable
          value={filteredCustomers}
          selection={selected}
          onSelectionChange={(e) => setSelected(e.value)}
          dataKey="id"
          loading={loadingCustomers}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage="No customers match this filter."
          size="small"
        >
          <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          <Column header="Name" body={(row) => `${row.first_name || ''} ${row.last_name || ''}`.trim()} />
          <Column field="phone_number" header="Phone Number" />
        </DataTable>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <Button
            label={`Send Broadcast (${selected.length})`}
            icon="pi pi-send"
            onClick={handleSendClick}
            loading={sending}
            disabled={sending || selected.length === 0}
          />
        </div>
      </div>

      <Dialog
        header="Save as Template"
        visible={saveTemplateVisible}
        style={{ width: '400px' }}
        modal
        onHide={() => setSaveTemplateVisible(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancel" text onClick={() => setSaveTemplateVisible(false)} />
            <Button label="Save" loading={savingTemplate} onClick={handleSaveTemplate} />
          </div>
        }
      >
        <div className="field">
          <label htmlFor="template-name" className="font-bold" style={{ display: 'block', marginBottom: '0.5rem' }}>
            Template Name:
          </label>
          <InputText
            id="template-name"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            style={{ width: '100%' }}
            placeholder="e.g. Holiday Closure"
            autoFocus
          />
        </div>
      </Dialog>
    </div>
  );
};

export default BroadcastSms;